import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import {
  useRequirementsViewStore,
  type RequirementsGroupBy,
} from '@/stores/requirementsViewStore'

const OPTIONS: Array<{ value: RequirementsGroupBy; label: string; description: string }> = [
  { value: 'hierarchy', label: 'Hierarchy', description: 'Group epics with their stories' },
  { value: 'persona',   label: 'Persona',   description: 'Group by the people each story serves' },
  { value: 'theme',     label: 'Theme',     description: 'Group by tag' },
  { value: 'status',    label: 'Status',    description: 'Group by completion state' },
  { value: 'priority',  label: 'Priority',  description: 'Group by importance level' },
]

const LABEL_BY_VALUE = Object.fromEntries(OPTIONS.map(o => [o.value, o.label])) as Record<RequirementsGroupBy, string>

/**
 * Pill button that opens a small popover for changing the group-by axis.
 * Backed by the store so Tree, Document, and Table views stay in sync.
 *
 * Note: the Table view also has its own segmented `Group:` control in its
 * trailing area for the historical hierarchy/status/priority/assignee axes —
 * that one writes to page-local state. This button writes to the store and
 * affects Tree/Document grouping (which uses richer axes including persona
 * and theme).
 */
export function GroupButton() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const groupBy = useRequirementsViewStore(s => s.groupBy)
  const setGroupBy = useRequirementsViewStore(s => s.setGroupBy)

  // Outside click + ESC close.
  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change how requirements are grouped"
        className="inline-flex items-center gap-1.5 rounded-lg cursor-pointer font-medium border-0 px-3 py-1.5 text-sm transition-colors"
        style={{
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-warm-200)',
          color: 'var(--color-fg)',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-bg-card)')}
      >
        <span style={{ color: 'var(--color-fg-muted)' }}>Group:</span>
        <span>{LABEL_BY_VALUE[groupBy]}</span>
        <ChevronDown className="h-3.5 w-3.5" style={{ color: 'var(--color-fg-muted)' }} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Group requirements by"
          className="absolute z-30 rounded-md overflow-hidden"
          style={{
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 160,
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 8px 24px -8px rgba(29,26,21,0.18), 0 2px 6px -2px rgba(29,26,21,0.06)',
            padding: 4,
          }}
        >
          {OPTIONS.map(o => {
            const active = groupBy === o.value
            return (
              <button
                key={o.value}
                role="option"
                aria-selected={active}
                onClick={() => { setGroupBy(o.value); setOpen(false) }}
                className="w-full flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer border-0 text-left text-sm"
                style={{
                  background: 'transparent',
                  color: 'var(--color-fg)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center', paddingTop: 2 }}>
                  {active && <Check className="h-3.5 w-3.5" style={{ color: 'var(--color-solution)' }} />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block">{o.label}</span>
                  <span
                    className="block"
                    style={{ fontSize: 11.5, color: 'var(--color-fg-muted)', fontWeight: 400, lineHeight: 1.35, marginTop: 1 }}
                  >
                    {o.description}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
